import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import Wrapper from '../../components/Wrapper';
import CollapsibleSection from '../../components/CollapsibleSection';
import { deviceApi } from '../../services/deviceApi';

const STAGE_BADGE = {
  GA: 'success',
  BETA: 'warning',
  ALPHA: 'secondary',
  DEPRECATED: 'danger',
};

const STAGES = ['ALPHA', 'BETA', 'GA', 'DEPRECATED'];

const compareSoftware = (newer, older) => {
  if (!older) return [];
  const changes = [];
  if (newer.version_tag !== older.version_tag) {
    changes.push({ field: 'Version', from: older.version_tag, to: newer.version_tag });
  }
  if (newer.stage !== older.stage) {
    changes.push({ field: 'Stage', from: older.stage, to: newer.stage });
  }
  if ((newer.release_notes || '') !== (older.release_notes || '')) {
    changes.push({ field: 'Release Notes', from: older.release_notes || '—', to: newer.release_notes || '—' });
  }
  const oldCompat = (older.compatible_hardware || []).join(', ') || '—';
  const newCompat = (newer.compatible_hardware || []).join(', ') || '—';
  if (oldCompat !== newCompat) {
    changes.push({ field: 'Compatible Hardware', from: oldCompat, to: newCompat });
  }
  return changes;
};

const emptyAddForm = () => ({
  software_type: 'DISPLAY',
  version_tag: '',
  stage: 'ALPHA',
  release_notes: '',
  compatible_hardware: [],
});

const SoftwareVersionGroup = ({ title, versions }) => (
  <div className="mb-4">
    <h5 className="fw-semibold mb-3">{title}</h5>
    {versions.length === 0 ? (
      <Alert variant="info">No {title.toLowerCase()} versions found.</Alert>
    ) : (
      versions.map((version, index) => {
        const older = versions[index + 1];
        const changes = compareSoftware(version, older);
        return (
          <CollapsibleSection
            key={version.id}
            title={(
              <>
                <span>{version.version_tag}</span>
                <Badge bg={STAGE_BADGE[version.stage] || 'secondary'} className="ms-2">{version.stage}</Badge>
                {index === 0 && <Badge bg="success" className="ms-2">Latest</Badge>}
              </>
            )}
          >
            {version.release_notes && (
              <p className="mb-3"><strong>Release notes:</strong> {version.release_notes}</p>
            )}
            <p className="text-muted mb-3">
              Compatible hardware: {(version.compatible_hardware || []).join(', ') || '—'}
            </p>
            {!older ? (
              <p className="text-muted mb-0">Earliest version in this category.</p>
            ) : changes.length === 0 ? (
              <p className="text-muted mb-0">
                No metadata changes from <Badge bg="light" text="dark">{older.version_tag}</Badge>.
              </p>
            ) : (
              <>
                <p className="mb-3">
                  Changes from <Badge bg="light" text="dark">{older.version_tag}</Badge>
                  {' '}to <Badge bg="primary">{version.version_tag}</Badge>
                </p>
                <div className="d-flex flex-column gap-2">
                  {changes.map((change) => (
                    <div key={`${change.field}-${version.id}`} className="border rounded p-2">
                      <strong>{change.field}</strong>
                      <div className="small text-muted mt-1">Previous: {change.from}</div>
                      <div className="small mt-1">Current: {change.to}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CollapsibleSection>
        );
      })
    )}
  </div>
);

const DeviceSoftwareVersion = () => {
  const [softwareList, setSoftwareList] = useState([]);
  const [hardwareList, setHardwareList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm());
  const [addError, setAddError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadSoftware = useCallback(() => {
    setLoading(true);
    deviceApi
      .getAllSoftware()
      .then(setSoftwareList)
      .catch((err) => setError(err?.response?.data?.error || 'Failed to load software versions.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadSoftware();
  }, [loadSoftware]);

  useEffect(() => {
    if (!showAddModal) return;
    deviceApi.getHardwareDropdown('device').then(setHardwareList).catch(() => setHardwareList([]));
  }, [showAddModal]);

  const displayVersions = useMemo(
    () => softwareList.filter((s) => s.software_type === 'DISPLAY'),
    [softwareList]
  );
  const deviceVersions = useMemo(
    () => softwareList.filter((s) => s.software_type === 'DEVICE'),
    [softwareList]
  );

  const toggleCompatibleHardware = (versionTag) => {
    setAddForm((prev) => {
      const selected = prev.compatible_hardware.includes(versionTag)
        ? prev.compatible_hardware.filter((tag) => tag !== versionTag)
        : [...prev.compatible_hardware, versionTag];
      return { ...prev, compatible_hardware: selected };
    });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!addForm.version_tag.trim()) {
      setAddError('Version tag is required.');
      return;
    }
    if (addForm.compatible_hardware.length === 0) {
      setAddError('Select at least one compatible hardware version.');
      return;
    }

    setSaving(true);
    setAddError('');
    try {
      await deviceApi.createSoftware({
        version_tag: addForm.version_tag.trim(),
        software_type: addForm.software_type,
        stage: addForm.stage,
        release_notes: addForm.release_notes.trim() || null,
        compatible_hardware: addForm.compatible_hardware,
      });
      setShowAddModal(false);
      setAddForm(emptyAddForm());
      loadSoftware();
    } catch (err) {
      setAddError(err?.response?.data?.error || 'Failed to create software version.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrapper>
      <div className="reports-container py-4 w-100">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
          <h1 className="fw-bolder mb-0">Software Version</h1>
          <Button variant="dark" onClick={() => { setAddForm(emptyAddForm()); setAddError(''); setShowAddModal(true); }}>
            <FaPlus className="me-2" />
            Add Version
          </Button>
        </div>

        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {!loading && !error && (
          <>
            <SoftwareVersionGroup title="Display Software" versions={displayVersions} />
            <SoftwareVersionGroup title="Device / Firmware Software" versions={deviceVersions} />
          </>
        )}
      </div>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Software Version</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddSubmit}>
          <Modal.Body>
            {addError && <Alert variant="danger">{addError}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>Software Type <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={addForm.software_type}
                onChange={(e) => setAddForm((prev) => ({ ...prev, software_type: e.target.value }))}
              >
                <option value="DISPLAY">Display Software</option>
                <option value="DEVICE">Device / Firmware Software</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Version Tag <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={addForm.version_tag}
                onChange={(e) => setAddForm((prev) => ({ ...prev, version_tag: e.target.value }))}
                placeholder="e.g. 1.2.0"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Stage</Form.Label>
              <Form.Select
                value={addForm.stage}
                onChange={(e) => setAddForm((prev) => ({ ...prev, stage: e.target.value }))}
              >
                {STAGES.map((stage) => (
                  <option key={stage} value={stage}>{stage}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Release Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={addForm.release_notes}
                onChange={(e) => setAddForm((prev) => ({ ...prev, release_notes: e.target.value }))}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Compatible Hardware <span className="text-danger">*</span></Form.Label>
              <div className="border rounded p-3 d-flex flex-column gap-2">
                {hardwareList.length === 0 ? (
                  <span className="text-muted">No hardware versions available.</span>
                ) : (
                  hardwareList.map((hw) => (
                    <Form.Check
                      key={hw.id}
                      type="checkbox"
                      id={`hw-${hw.id}`}
                      label={hw.version_tag}
                      checked={addForm.compatible_hardware.includes(hw.version_tag)}
                      onChange={() => toggleCompatibleHardware(hw.version_tag)}
                    />
                  ))
                )}
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : 'Add Version'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Wrapper>
  );
};

export default DeviceSoftwareVersion;
